import React, { useState, useEffect, useMemo } from 'react';
import packageJson from '../../package.json';
import { getRelativeTime } from '../lib/time';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  ShoppingCart,
  FolderOpen,
  Users,
  Building2,
  CalendarHeart,
  Menu,
  LogOut,
  X,
  PanelLeftClose,
  PanelLeft,
  Bell,
  Check,
  ClipboardCheck,

  Settings,
  Palmtree,
  Receipt,
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { ThemeToggle } from './theme-toggle';
import { useNotifications } from '../hooks/use-notifications';
import { useApprovalCounts } from '../contexts/ApprovalCountsContext';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

type MenuItem = {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  shortcut?: string;
  badge?: number;
  disabled?: boolean;
};

type MenuSection = {
  title: string;
  items: MenuItem[];
};

const assignSequentialShortcuts = (items: MenuItem[], startAt = 1): MenuItem[] => {
  let nextShortcut = startAt;

  return items.map((item) => {
    if (nextShortcut > 9) {
      return item;
    }

    const itemWithShortcut = { ...item, shortcut: String(nextShortcut) };
    nextShortcut += 1;
    return itemWithShortcut;
  });
};

const Layout: React.FC = () => {
  const { logout, user, tenant } = useAuth();
  const appVersion = packageJson.version;

  // Get logo URL handling both relative and absolute paths
  const getLogoUrl = () => {
    if (!tenant?.logoUrl) return null;
    if (tenant.logoUrl.startsWith('http')) return tenant.logoUrl;
    return `${API_URL}${tenant.logoUrl}`;
  };
  const logoUrl = getLogoUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const {
    pendingTimesheetCount,
    pendingLeaveCount,
    pendingExpenseCount,
    pendingTenantRequestCount,
  } = useApprovalCounts();
  const { t } = useTranslation();

  // Use localized relative time utility from lib/time

  // Track whether Cmd/Ctrl is currently held to show shortcut UI
  const [isModPressed, setIsModPressed] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) setIsModPressed(true);
    };
    const up = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) setIsModPressed(false);
    };
    const onBlur = () => setIsModPressed(false);

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  const menuSections = useMemo(() => {
    const sections: MenuSection[] = [];

    const documentsItem: MenuItem = {
      text: t('menu.documentsOperational', 'Documenti'),
      icon: FolderOpen,
      path: '/documents',
    };

    const workbenchItemsBase: MenuItem[] = user?.role
      && [UserRole.OPERATOR, UserRole.MANAGER, UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.SUPER_ADMIN].includes(user.role)
      ? [
          { text: t('menu.dashboard'), icon: LayoutDashboard, path: '/' },
          { text: t('menu.monthlyView'), icon: Calendar, path: '/timesheet' },
          { text: t('menu.yearlySummary'), icon: FileText, path: '/yearly-summary' },
          { text: t('menu.leaveRequests', 'Leave Requests'), icon: Palmtree, path: '/leave-requests' },
          { text: t('menu.expenseClaims', 'Note spese'), icon: Receipt, path: '/expense-claims' },
          documentsItem,
        ]
      : [];

    const adminFlowItemsBase: MenuItem[] = [
      ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [
            { text: t('menu.approvals', 'Approvals'), icon: ClipboardCheck, path: '/approvals', badge: pendingTimesheetCount },
          ]
        : []),
      ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.leaveApprovals', 'Leave Approvals'), icon: Palmtree, path: '/leave-approvals', badge: pendingLeaveCount }]
        : []),
      ...(user?.role && [UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.expenseClaimsApprovals', 'Approvazioni note spese'), icon: Receipt, path: '/expense-claims-approvals', badge: pendingExpenseCount }]
        : []),
      ...(user?.role && [UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.tenantRequests', 'Tenant Requests'), icon: ClipboardCheck, path: '/admin/tenant-requests', badge: pendingTenantRequestCount }]
        : []),
    ];

    const businessItems: MenuItem[] = [
      ...(user?.role && [UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.clientsSuppliers'), icon: Building2, path: '/client-supplier' }]
        : []),

      ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.orders'), icon: ShoppingCart, path: '/orders' }]
        : []),
      ...(user?.role && [UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.users'), icon: Users, path: '/admin/users' }]
        : []),
      ...(user?.role && [UserRole.ADMIN, UserRole.MANAGER, UserRole.SUPER_ADMIN].includes(user.role)
        ? [{ text: t('menu.nationalHolidays'), icon: CalendarHeart, path: '/national-holidays' }]
        : []),
    ];

    const workbenchItems = assignSequentialShortcuts(workbenchItemsBase);
    const adminFlowItems = assignSequentialShortcuts(adminFlowItemsBase, workbenchItems.length + 1);

    const settingsItems: MenuItem[] = user?.role
      ? [
          ...(user.role === UserRole.SUPER_ADMIN
            ? [{ text: t('menu.tenants'), icon: Building2, path: '/admin/tenants' }]
            : []),
          { text: t('menu.settings'), icon: Settings, path: '/settings', shortcut: '9' },
        ]
      : [];

    if (workbenchItems.length > 0) {
      sections.push({
        title: t('menu.sections.workbench', 'Operativita'),
        items: workbenchItems,
      });
    }
    if (adminFlowItems.length > 0) {
      sections.push({
        title: t('menu.sections.approvals', 'Flussi approvativi'),
        items: adminFlowItems,
      });
    }
    if (businessItems.length > 0) {
      sections.push({
        title: t('menu.sections.business', 'Gestione aziendale'),
        items: businessItems,
      });
    }
    if (settingsItems.length > 0) {
      sections.push({
        title: t('menu.sections.settings', 'Configuration'),
        items: settingsItems,
      });
    }

    return sections;
  }, [
    user?.role,
    t,
    pendingTimesheetCount,
    pendingLeaveCount,
    pendingExpenseCount,
    pendingTenantRequestCount,
  ]);

  const menuItems = useMemo(
    () => menuSections.flatMap((section) => section.items),
    [menuSections],
  );

  // A child route (e.g. /approvals/12) keeps its section item lit.
  const isItemActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger with Cmd/Ctrl key
      if (!(e.metaKey || e.ctrlKey)) return;

      // Don't trigger if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      const key = e.key;
      const menuItem = menuItems.find((item) => item.shortcut === key);

      if (menuItem && !menuItem.disabled) {
        e.preventDefault();
        navigate(menuItem.path);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuItems, navigate]);

  const NavContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <div className="flex h-full min-h-0 flex-col">
      {/* The list scrolls on its own so version and logout stay reachable on short screens */}
      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        <nav className={cn('space-y-1', isCollapsed ? 'px-2' : 'px-2')}>
          {menuSections.map((section, sectionIndex) => (
            <React.Fragment key={`section-${sectionIndex}`}>
              {sectionIndex > 0 && <Separator className="my-2" />}
              {!isCollapsed && (
                <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  {section.title}
                </p>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item.path);
                const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
                const modKey = isMac ? '⌘' : 'Ctrl+';

                const badgeCount = item.badge;
                const isDisabled = Boolean(item.disabled);

                const button = (
                  <button
                    key={item.path}
                    onClick={() => {
                      if (isDisabled) return;
                      navigate(item.path);
                      setOpen(false);
                    }}
                    disabled={isDisabled}
                    className={cn(
                      'w-full flex items-center rounded-lg text-sm font-medium transition-colors relative',
                      isCollapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
                      isDisabled
                        ? 'opacity-45 text-muted-foreground/70 cursor-not-allowed'
                        : isActive
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <div className="relative">
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {isCollapsed && badgeCount && badgeCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[9px] font-medium text-white flex items-center justify-center">
                          {badgeCount > 9 ? '9+' : badgeCount}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <>
                        <span className="truncate text-left flex-1">{item.text}</span>
                        {badgeCount && badgeCount > 0 && (
                          <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </Badge>
                        )}
                        {item.shortcut && !badgeCount && isModPressed && (
                          <kbd className="inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                            {modKey}{item.shortcut}
                          </kbd>
                        )}
                      </>
                    )}
                  </button>
                );

                if (isCollapsed) {
                  return (
                    <Tooltip key={item.path} delayDuration={0}>
                      <TooltipTrigger asChild>{button}</TooltipTrigger>
                      <TooltipContent side="right" sideOffset={10} className="flex items-center gap-2">
                        <span>{item.text}</span>
                        {item.shortcut && isModPressed && (
                          <kbd className="inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                            {modKey}{item.shortcut}
                          </kbd>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return button;
              })}
            </React.Fragment>
          ))}
        </nav>
      </div>
      <div className={cn('px-4 pb-2', isCollapsed && 'px-2 text-center')}>
        <p className="text-[11px] text-muted-foreground/80">{t('common.version', 'Version')} {appVersion}</p>
      </div>
      <Separator />
      <div className={cn('p-4', isCollapsed && 'px-2')}>
        {isCollapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-full text-muted-foreground hover:text-foreground"
                onClick={logout}
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {t('menu.logout')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
            onClick={logout}
          >
            <LogOut className="h-5 w-5" />
            {t('menu.logout')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center px-4">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-64 flex-col p-0 [&>button]:hidden">
              <div className="flex shrink-0 items-center justify-between p-4 border-b">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={tenant?.name || t('app.title')}
                    className="h-8 max-w-[140px] object-contain"
                  />
                ) : (
                  <span className="font-semibold text-orange-600">{tenant?.name || t('app.title')}</span>
                )}
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <NavContent isCollapsed={false} />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 ml-2 md:ml-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={tenant?.name || t('app.title')}
                className="h-8 max-w-[160px] object-contain"
              />
            ) : (
              <span className="font-semibold text-orange-600">{tenant?.name || t('app.title')}</span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>

            {/* Notification Bell */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-[10px] font-medium text-white flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-4 border-b">
                  <h4 className="font-semibold">{t('notifications.title', 'Notifications')}</h4>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7">
                      <Check className="h-3 w-3 mr-1" />
                      {t('notifications.markAllRead', 'Mark all read')}
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length > 0 ? (
                    <div className="divide-y">
                      {notifications.map((notification) => {
                        const timeStr = getRelativeTime(notification.createdAt);
                        return (
                          <button
                            key={notification.id}
                            onClick={async () => {
                              try {
                                await markAsRead(notification.id);
                              } catch (e) {
                                // ignore mark-as-read errors
                              } finally {
                                if (notification.link) {
                                  if (notification.link.startsWith('http')) {
                                    window.open(notification.link, '_blank');
                                  } else {
                                    navigate(notification.link);
                                  }
                                }
                              }
                            }}
                            className={cn(
                              'w-full text-left p-4 hover:bg-muted transition-colors',
                              !notification.read && 'bg-orange-50 dark:bg-orange-900/10'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              {!notification.read && (
                                <span className="mt-1.5 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                              )}
                              <div className={cn('flex-1 min-w-0', notification.read && 'ml-5')}>
                                <p className="font-medium text-sm break-words whitespace-normal">{notification.title}</p>
                                <p className="text-sm text-muted-foreground break-words whitespace-normal">{notification.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{timeStr}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>{t('notifications.empty', 'No notifications')}</p>
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside
          className={cn(
            'hidden md:flex flex-col border-r bg-background h-[calc(100vh-3.5rem)] sticky top-14 transition-all duration-300',
            collapsed ? 'w-16' : 'w-64'
          )}
        >
          {/* Collapse Toggle */}
          <div className={cn('flex items-center border-b h-12', collapsed ? 'justify-center' : 'justify-end px-2')}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCollapsed(!collapsed)}
                  className="h-8 w-8"
                >
                  {collapsed ? (
                    <PanelLeft className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {collapsed ? t('sidebar.expand', 'Expand sidebar') : t('sidebar.collapse', 'Collapse sidebar')}
              </TooltipContent>
            </Tooltip>
          </div>
          <NavContent isCollapsed={collapsed} />
        </aside>

        {/* Main Content */}
        {/* overflow-x: clip cuts horizontal overflow without turning main into a scroll container, so sticky bars inside pages pin to the viewport */}
        <main className="min-w-0 flex-1 [overflow-x:clip] pb-[env(safe-area-inset-bottom)]">
          <Outlet />
        </main>
      </div>

    </div>
    </TooltipProvider>
  );
};

export default Layout;
